import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import ChatHeader from "./ChatHeader";
import ChatSidebar from "./ChatSidebar";
import MessageList from "./MessageList";
import WelcomeScreen from "./WelcomeScreen";
import ChatInput from "./ChatInput";
import { useAIChat } from "./useAIChat";
import "../../styles/ai-panel.css";

/**
 * AIChatPanel — floating panel + full-screen toggle.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 */
export default function AIChatPanel({ open, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const {
    welcome, conversationId, conversationTitle,
    messages, history, streaming, error,
    send, stop, newConversation, openConversation, refreshHistory,
  } = useAIChat({ active: open });

  // Close sidebar when collapsing back to floating mode
  useEffect(() => { if (!expanded) setSidebarOpen(false); }, [expanded]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSend = (text) => {
    send(text, { page: location.pathname, search: location.search });
  };

  const handleSuggestion = (text) => handleSend(text);

  const handleOpen = async (id) => {
    await openConversation(id);
    if (!expanded) setSidebarOpen(false);
  };

  const handleNew = () => {
    newConversation();
    if (!expanded) setSidebarOpen(false);
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={`ai-shell ${expanded ? "is-expanded" : ""}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          {expanded ? <div className="ai-shell-backdrop" onClick={onClose} /> : null}
          <motion.div
            className="ai-panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            {(expanded || sidebarOpen) ? (
              <ChatSidebar
                history={history}
                activeId={conversationId}
                onOpen={handleOpen}
                onNew={handleNew}
                onMutated={refreshHistory}
              />
            ) : null}

            <div className="ai-main">
              <ChatHeader
                title={conversationId ? conversationTitle : ""}
                expanded={expanded}
                onToggleSidebar={() => setSidebarOpen((v) => !v)}
                onToggleExpand={() => setExpanded((v) => !v)}
                onClose={onClose}
                onNew={handleNew}
              />

              {messages.length === 0 ? (
                <WelcomeScreen welcome={welcome} onPickSuggestion={handleSuggestion} />
              ) : (
                <MessageList messages={messages} error={error} />
              )}

              <ChatInput onSend={handleSend} onStop={stop} streaming={streaming} />
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
